/**
 * App-Header: zeigt data_stand + generated_at (SCHEMA_MAPPING §2.1),
 * Theme-Toggle und Logout. Semantisches <header>.
 */
import { Link } from 'react-router-dom';
import type { Jahreswechsel, Meta, Vollstaendigkeit } from '../types/snapshot';
import { useTheme } from '../lib/useTheme';
import { useAuth } from '../auth/AuthContext';
import { jahreAusMeta } from '../lib/jahre';

function formatGeneratedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('de-DE', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(d);
}

/**
 * Dezenter Datenstands-Hinweis (S2.4 / D-047). null = kein Hinweis:
 * Feld fehlt (alter Cache), oder alle Gesellschaften auf dem Soll-Monat.
 */
export function vollstaendigkeitHinweis(
  v: Vollstaendigkeit | null | undefined,
  berichtsjahr: number,
): string | null {
  // fehlend defensiv lesen: Blob-Daten sind externe, ungepruefte Eingabe (Review-Fix #3).
  const fehlend = v?.fehlend ?? [];
  if (!v || v.soll_monat == null || fehlend.length === 0) return null;
  // D-089: soll_monat ist ein Monat des Berichtsjahrs (nicht mehr fest 2026).
  const mm = `${String(v.soll_monat).padStart(2, '0')}/${berichtsjahr}`;
  if (fehlend.length === 1) return `${fehlend[0]} fehlt (${mm})`;
  return `${fehlend.length} Gesellschaften fehlen (${mm})`;
}

/** D-089: so lange nach dem Wechsel erinnert der Header an die Anfangsbestände. */
export const JAHRESWECHSEL_HINWEIS_TAGE = 60;
const TAG_MS = 24 * 60 * 60 * 1000;

/** ISO → „TT.MM.JJJJ" in deutscher Zeit (unabhängig von der TZ des Browsers). */
function formatDatumDe(d: Date): string {
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Europe/Berlin',
  }).format(d);
}

/**
 * D-089 (a): Hinweis nach vollzogenem Jahreswechsel, solange umgestellt_am jünger
 * als 60 Tage ist. `heute` ist Parameter (zeitabhängige Prüfung, testbar).
 * null = kein Hinweis: Feld fehlt (alter Snapshot), kein/unlesbares Datum, älter.
 */
export function jahreswechselVollzogenHinweis(
  jw: Jahreswechsel | null | undefined,
  heute: Date,
): string | null {
  if (!jw?.umgestellt_am) return null;
  const umgestellt = new Date(jw.umgestellt_am);
  if (Number.isNaN(umgestellt.getTime())) return null;
  if (heute.getTime() - umgestellt.getTime() >= JAHRESWECHSEL_HINWEIS_TAGE * TAG_MS) return null;
  const bericht = jw.kontrollbericht ? `Kontrollbericht ${jw.kontrollbericht}, ` : 'Kontrollbericht ';
  return (
    `Jahreswechsel ${jw.vorjahr} → ${jw.berichtsjahr} am ${formatDatumDe(umgestellt)} ` +
    `vollzogen — Anfangsbestände prüfen (${bericht}lokal im SR-Repo output/jahreswechsel/)`
  );
}

/** wartet_auf defensiv lesen: Blob-Daten sind externe, ungeprüfte Eingabe. */
function wartetAuf(jw: Jahreswechsel | null | undefined) {
  return Array.isArray(jw?.wartet_auf) ? jw.wartet_auf : [];
}

/**
 * D-089 (b): Hinweis auf den anstehenden Jahreswechsel, solange Gesellschaften
 * fehlen. null = kein Hinweis (naechstes_jahr nicht gesetzt oder nichts offen).
 */
export function jahreswechselAnstehendHinweis(
  jw: Jahreswechsel | null | undefined,
): string | null {
  const offen = wartetAuf(jw);
  if (jw?.naechstes_jahr == null || offen.length === 0) return null;
  const namen = offen.map((w) => w.gesellschaft).join(', ');
  return `Jahreswechsel ${jw.naechstes_jahr} steht an — es fehlen: ${namen}`;
}

export function Header({ meta, heute }: { meta: Meta | null; heute?: Date }) {
  const { theme, toggle } = useTheme();
  const { logout } = useAuth();
  const { bj } = jahreAusMeta(meta);
  const hinweisVollstaendig = meta ? vollstaendigkeitHinweis(meta.vollstaendigkeit, bj) : null;
  const hinweisVollzogen = jahreswechselVollzogenHinweis(meta?.jahreswechsel, heute ?? new Date());
  const hinweisAnstehend = jahreswechselAnstehendHinweis(meta?.jahreswechsel);
  const anstehendGruende = wartetAuf(meta?.jahreswechsel)
    .map((w) => `${w.gesellschaft}: ${w.grund}`)
    .join('\n');

  return (
    <header className="border-b border-border bg-surface">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-3">
        <div className="flex items-baseline gap-3">
          <Link to="/" className="text-lg font-semibold text-text">
            LBU Quarterly Reporting
          </Link>
          {meta && (
            <span className="rounded bg-surface-2 px-2 py-0.5 text-xs text-text-muted">
              {meta.data_stand}
            </span>
          )}
          {hinweisVollstaendig && (
            <span
              className="rounded bg-yellow/10 px-2 py-0.5 text-xs text-yellow"
              title="SuSa-Datenstand unvollständig (D-047)"
            >
              ⚠ {hinweisVollstaendig}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-sm">
          {meta && (
            <span className="text-text-muted">Stand: {formatGeneratedAt(meta.generated_at)}</span>
          )}
          <button
            type="button"
            onClick={toggle}
            className="rounded border border-border px-2 py-1 text-text-muted hover:bg-surface-2"
            aria-label={theme === 'dark' ? 'Zu hellem Modus wechseln' : 'Zu dunklem Modus wechseln'}
          >
            {theme === 'dark' ? 'Hell' : 'Dunkel'}
          </button>
          <button
            type="button"
            onClick={() => void logout()}
            className="rounded bg-accent px-3 py-1 text-accent-fg hover:opacity-90"
          >
            Abmelden
          </button>
        </div>
      </div>
      {/* D-089: dezente Jahreswechsel-Hinweise in eigener Zeile (lange Texte
          sollen die Kopfzeile nicht umbrechen). */}
      {(hinweisVollzogen || hinweisAnstehend) && (
        <div className="mx-auto flex max-w-6xl flex-col gap-1 px-4 pb-2 text-xs text-text-muted">
          {hinweisVollzogen && <p title="Jahreswechsel (D-089)">ⓘ {hinweisVollzogen}</p>}
          {hinweisAnstehend && (
            <p className="text-yellow" title={anstehendGruende}>
              ⚠ {hinweisAnstehend}
            </p>
          )}
        </div>
      )}
    </header>
  );
}
